import React, { useState, useEffect } from "react"
import { api } from "aldoo-ra/API"
import { url } from "aldoo-ra/CMS/url"
import Typography from "aldoo-ra/Typography"
// import Value from "aldoo-ra/Value" // Uncomment when reverting to site language
import { ChevronRightIcon } from "@heroicons/react/24/outline"
import TextBundle from "aldoo-ra/CMS/text-bundle"

// Initialize API
const hcAPI = api("help-center")

/**
 * FeaturedCategories component for the Help Center
 * Displays categories from a referenced category in a card layout
 * Now filters out draft content
 */
const FeaturedCategories = () => {
  const texts = TextBundle({ id: "help-center" })

  const [categories, setCategories] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [featuredTitle, setFeaturedTitle] = useState("Products")
  const [parentCategorySlug, setParentCategorySlug] = useState("")

  // Force English for help center
  const [currentLanguage] = useState("en")
  
  // To revert to site language, uncomment below and comment out the line above:
  // const [currentLanguage] = Value({
  //   key: "current-site-language",
  //   defaultValue: "en",
  //   persistKey: "current-site-language",
  // })

  // Helper function to check if an item is published
  const isPublished = (item) => {
    return item.status === "published"
  }

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true)

        // Step 1: Fetch the featured category to get references
        const featuredResponse = await hcAPI({
          get_content: {
            contentURL: "/category/featured",
            language: currentLanguage,
          },
        })

        // Check if featured has references to other categories
        if (featuredResponse?.content?.categories?.length > 0) {
          // Filter categories to only include published ones
          const publishedCategories =
            featuredResponse.content.categories.filter(isPublished)

          if (publishedCategories.length === 0) {
            setCategories([])
            setIsLoading(false)
            return
          }

          // Get the first referenced category (e.g. "products")
          const mainCategory = publishedCategories[0]

          // Store the parent category slug for URL building
          setParentCategorySlug(mainCategory.slug)

          // Step 2: Fetch the referenced category content
          const mainCategoryData = await hcAPI({
            get_content: {
              contentURL: `/category/${mainCategory.slug}`,
              language: currentLanguage,
            },
          })

          // Update the section title if needed
          if (mainCategory.label) {
            setFeaturedTitle(mainCategory.label)
          }

          // Step 3: For each category in the main category, fetch its details and articles
          if (mainCategoryData?.content?.categories?.length > 0) {
            // Filter subcategories to only include published ones
            const publishedSubcategories =
              mainCategoryData.content.categories.filter(isPublished)

            const categoriesWithArticles = await Promise.all(
              publishedSubcategories.map(async (categoryRef) => {
                try {
                  // Fetch the full category data to get its articles
                  const categoryData = await hcAPI({
                    get_content: {
                      contentURL: `/category/${categoryRef.slug}`,
                      language: currentLanguage,
                    },
                  })

                  // Get the basic category info
                  const categoryInfo = {
                    _id: categoryRef._id || categoryData._id,
                    label: categoryRef.label || categoryData.content?.name,
                    slug: categoryRef.slug,
                    icon: categoryRef.icon || categoryData.content?.icon,
                    status: categoryRef.status || categoryData.content?.status,
                  }

                  // Filter articles to only include published ones
                  const publishedArticles = (
                    categoryData?.content?.articles || []
                  ).filter(isPublished)

                  // Add the articles array (filtered for published only)
                  return {
                    ...categoryInfo,
                    articles: publishedArticles,
                  }
                } catch (error) {
                  console.error(
                    `Error fetching category content for ${categoryRef.slug}:`,
                    error
                  )
                  return {
                    ...categoryRef,
                    articles: [],
                  }
                }
              })
            )

            // Filter out any categories that might have draft status
            const publishedResults = categoriesWithArticles.filter(isPublished)
            setCategories(publishedResults)
          } else {
            setCategories([])
          }
        } else {
          setCategories([])
        }

        setIsLoading(false)
      } catch (err) {
        console.error("Error fetching featured categories:", err)
        setError("Failed to load featured categories")
        setIsLoading(false)
      }
    }

    fetchCategories()
  }, [currentLanguage])

  // Handle category click with hybrid approach (SEO + JS navigation)
  const handleCategoryClick = (e, slug) => {
    e.preventDefault()

    // Update URL and notify content loader
    const fullPath = parentCategorySlug ? `${parentCategorySlug}/${slug}` : slug

    // Trigger URL update and content loading
    if (window.loadHelpCenterContent) {
      window.loadHelpCenterContent("category", slug, parentCategorySlug)
    } else {
      // Fallback to direct navigation if global handler isn't available
      window.location.href = `/help-center/category/${fullPath}`
    }
  }

  // Handle article click with hybrid approach
  const handleArticleClick = (e, categorySlug, articleSlug) => {
    e.preventDefault()

    // Determine the category path
    const categoryPath = parentCategorySlug
      ? `${parentCategorySlug}/${categorySlug}`
      : categorySlug

    // Trigger URL update and content loading
    if (window.loadHelpCenterContent) {
      window.loadHelpCenterContent("article", articleSlug, categoryPath)
    } else {
      // Fallback to direct navigation
      window.location.href = `/help-center/category/${categoryPath}/article/${articleSlug}`
    }
  }

  if (isLoading) {
    return (
      <div className="py-8 text-center">
        {/* Spinner is hidden (the whole page has a custom loader) */}
        {/* <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div> */}

        <Typography variant="txt1_dim" className="mt-2">
          {texts?.loading_featured_cat || "Loading featured categories..."}
        </Typography>
      </div>
    )
  }

  if (error) {
    return <Typography variant="error">{error}</Typography>
  }

  if (!categories || categories.length === 0) {
    return null
  }

  return (
    <div className="mt-14">
      <Typography
        variant="h2"
        className="text-center md:text-left mb-6 md:mb-11"
      >
        {featuredTitle}
      </Typography>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-7">
        {categories.map((category) => (
          <div
            key={category._id}
            className="border border-bordered bg-paper rounded-[30px] overflow-hidden transition-shadow px-[26px]"
          >
            {/* Category header with hybrid approach (SEO links + JS navigation) */}
            <a
              href={`/help-center/category/${parentCategorySlug}/${category.slug}`}
              className="h-[100px] flex items-center cursor-pointer border-b border-bordered no-underline py-6"
              onClick={(e) => handleCategoryClick(e, category.slug)}
              aria-label={`View ${category.label} category`}
            >
              {category.icon && (
                <div className="mr-[10px]">
                  <img
                    src={url(category.icon)}
                    alt={`${category.label} icon`}
                    className="w-[50px] h-[50px] object-cover"
                  />
                </div>
              )}

              <Typography
                variant="h4"
                className="text-text hover:text-gray_800"
              >
                {category.label}
              </Typography>

              <div className="w-[30px] h-[30px] flex items-center justify-center bg-primary_lt rounded-full ml-auto">
                <ChevronRightIcon className="w-[18px] h-[18px] text-text stroke-2" />
              </div>
            </a>

            {/* Articles list with hybrid approach */}
            <div className="py-4">
              {category.articles &&
                category.articles.slice(0, 3).map((article) => (
                  <a
                    key={article._id}
                    href={`/help-center/category/${parentCategorySlug}/${category.slug}/article/${article.slug}`}
                    className="h-[68px] flex items-center gap-4 hover:bg-gradient_showcase_card rounded-[20px] cursor-pointer no-underline px-2"
                    onClick={(e) =>
                      handleArticleClick(e, category.slug, article.slug)
                    }
                    aria-label={`Read ${article.label} article`}
                  >
                    <Typography variant="body1" className="flex-grow my-0">
                      {article.label}
                    </Typography>

                    <ChevronRightIcon className="w-[18px] h-[18px] text-gray_700 stroke-2 flex-shrink-0 mr-1" />
                  </a>
                ))}

              {/* Show message if no articles */}
              {(!category.articles || category.articles.length === 0) && (
                <div className="px-6 py-4italic border-t">
                  <Typography variant="body1_dim">
                    {texts?.no_articles_in_cat || "No articles available"}
                  </Typography>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Add this helper function to the window object to facilitate communication with other components
if (typeof window !== "undefined") {
  window.initHelpCenterNavigation = (loadContentFunction) => {
    window.loadHelpCenterContent = loadContentFunction
  }
}

export default FeaturedCategories
